import { createContext, useContext, useEffect, useState } from "react";
import { useUser } from "./user";
import axios from "axios";

// API base URL configuration
const API_BASE_URL = process.env.NODE_ENV === "production" 
  ? "https://thebooth.gg" 
  : "http://localhost:8000";

const LeaguesContext = createContext();

export function useLeagues() {
  return useContext(LeaguesContext);
}

export function LeaguesProvider(props) {
  const user = useUser();
  const [leagues, setLeagues] = useState([]);
  const [loadingLeagues, setLoadingLeagues] = useState(false);

  async function addLeague(leagueId, leagueData, ownedTeam, platform) {
    try {
      const response = await axios.post(`${API_BASE_URL}/add-free-league`, {
        userId: user.current.id,
        userEmail: user.current.email,
        leagueId: leagueId,
        leagueData: leagueData,
        ownedTeam: ownedTeam,
        platform: platform,
        headers: {
          Authorization: `${user.current.id}:${user.token}`,
        },
      });

      if (response.data.success) {
        await init();
        return { success: true };
      }
      return { success: false, error: response.data.error || "UNKNOWN_ERROR" };
    } catch (error) {
      const errorCode = error.response?.data?.error;
      return { success: false, error: errorCode || "UNKNOWN_ERROR" };
    }
  }

  async function add(leagueId, email, platform) {
    try {
      const response = await axios.post(`${API_BASE_URL}/addLeague`, {
        leagueId: leagueId,
        email: email,
        platform: platform,
        headers: {
          Authorization: `${user.current.id}:${user.token}`,
        },
      });

      if (response.data) {
        return response.data;
      }
      return { error: "UNKNOWN_ERROR" };
    } catch (error) {
      console.error("Error adding league:", error);
      const errorCode = error.response?.data?.error;
      return { error: errorCode || "UNKNOWN_ERROR" };
    }
  }

  async function updateLeague(leagueId, emails, subscriptions) {
    try {
      await axios.post(`${API_BASE_URL}/updateMembers`, {
        emails: emails,
        subscriptions: subscriptions,
        headers: {
          Authorization: `${user.current.id}:${user.token}`,
        },
      });
      await init();
    } catch (error) {
      console.log(error);
    }
  }

  async function toggleLeagueDisabled(leagueId, disabled) {
    try {
      await axios.post(`${API_BASE_URL}/leagues/${leagueId}/disable`, {
        disabled: disabled,
        headers: {
          Authorization: `${user.current.id}:${user.token}`,
        },
      });
      await init();
    } catch (error) {
      console.log(error);
    }
  }

  async function getInviteLink(leagueId) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/leagues/${leagueId}/invite-link`,
        {
          headers: {
            Authorization: `${user.current.id}:${user.token}`,
          },
        }
      );
      return response.data.inviteToken;
    } catch (error) {
      console.error("Error getting invite link:", error);
      return null;
    }
  }

  async function resetMemberEmail(leagueId, memberId) {
    try {
      await axios.post(
        `${API_BASE_URL}/leagues/${leagueId}/members/${memberId}/reset-email`,
        {
          headers: {
            Authorization: `${user.current.id}:${user.token}`,
          },
        }
      );
      await init();
    } catch (error) {
      console.log(error);
    }
  }

  async function init() {
    if (user.current !== null) {
      setLoadingLeagues(true);
      const leagueInfos = await axios
        .post(`${API_BASE_URL}/leagues`, {
          headers: {
            Authorization: `${user.current.id}:${user.token}`,
          },
        })
        .then((response) => response.data)
        .catch(function (error) {
          return { leagues: [] };
        });
      setLoadingLeagues(false);
      setLeagues(leagueInfos.leagues);
    }
  }

  useEffect(() => {
    init();
    // Depend on the user id, not the whole context-value object: a
    // redundant SIGNED_IN event (e.g. Supabase re-firing on window/tab
    // focus regain) produces a new object reference for the same user,
    // which would otherwise retrigger this refetch and flip `loading`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.current?.id]);

  return (
    <LeaguesContext.Provider
      value={{
        current: leagues,
        add,
        addLeague,
        updateLeague,
        toggleLeagueDisabled,
        getInviteLink,
        resetMemberEmail,
        loading: loadingLeagues,
      }}
    >
      {props.children}
    </LeaguesContext.Provider>
  );
}
